import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { customerName } from '@/utils/format';
import type { CustomerRecord } from '@/domain/types';

// The Overrides-tab audit trail folded in from the retired /approvals screen
// (tv-i0b): a sticky "declined - collect a deposit" flag banner, plus a
// collapsible recently-resolved disclosure so the approve/reject history the
// standalone screen carried survives on the Ops worklist. Light theme tokens.

const REQUESTER_LABEL: Record<string, string> = {
  georgina: 'Georgina',
  'studio-manager': 'Studio manager',
  'bookings-agent': 'Bookings agent',
  agent: 'Agent',
  artist: 'Artist',
  apprentice: 'Apprentice',
  owner: 'Owner',
  'front-desk': 'Front desk',
  Console: 'Console',
};
const label = (key: string): string => REQUESTER_LABEL[key] ?? key;

export default function OpsOverridesAudit() {
  const overrideRequests = useBrainStore((s) => s.overrideRequests);
  const declineFlags = useBrainStore((s) => s.noDepositDeclineFlags);
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const [open, setOpen] = useState(false);

  const nameFor = (customerId: string): string => {
    const c: CustomerRecord | undefined = customers[customerId];
    return c ? customerName(c) : customerId.slice(-6);
  };

  // Active (unresolved) decline flags - the sticky "agent must collect a deposit" state.
  const activeDeclines = useMemo(
    () => Object.values(declineFlags).filter((f) => !f.resolvedAt),
    [declineFlags],
  );

  const resolved = useMemo(
    () =>
      Object.values(overrideRequests)
        .filter((r) => r.status !== 'pending')
        .sort((a, b) => (b.resolvedAt ?? '').localeCompare(a.resolvedAt ?? '')),
    [overrideRequests],
  );

  if (activeDeclines.length === 0 && resolved.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 mb-4">
      {activeDeclines.length > 0 ? (
        <div
          className="rounded-md border border-blood/40 bg-blood/5 px-3 py-2.5"
          data-testid="ops-decline-flag-banner"
        >
          <p className="font-inter text-xs text-blood flex items-center gap-1.5">
            <AlertTriangle size={13} />
            {activeDeclines.length === 1
              ? '1 customer declined - agent must collect a deposit'
              : `${activeDeclines.length} customers declined - agent must collect a deposit`}
          </p>
          <ul className="mt-1.5 flex flex-col gap-0.5">
            {activeDeclines.slice(0, 6).map((f) => (
              <li
                key={f.id}
                className="font-mono text-[10px] uppercase tracking-widest text-cream-faint"
                data-testid={`ops-decline-flag-${f.customerId}`}
              >
                {nameFor(f.customerId)}
                {f.reason ? <span className="normal-case tracking-normal"> · {f.reason}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {resolved.length > 0 ? (
        <div className="rounded-md border border-gold-dim/25 bg-ink-soft/30">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 text-left"
            data-testid="ops-resolved-toggle"
            aria-expanded={open}
          >
            {open ? <ChevronDown size={14} className="text-cream-faint" /> : <ChevronRight size={14} className="text-cream-faint" />}
            <span className="label-small-caps text-cream">Recently resolved</span>
            <span className="font-mono text-[11px] text-cream-faint">{resolved.length}</span>
          </button>
          {open ? (
            <ul className="px-3 pb-3 flex flex-col gap-1.5" data-testid="ops-resolved-list">
              {resolved.slice(0, 12).map((req) => {
                const booking = bookings[req.bookingId];
                const customer = booking ? customers[booking.customerId] : undefined;
                const approved = req.status === 'approved';
                return (
                  <li
                    key={req.id}
                    className="rounded-md border border-gold-dim/20 bg-ink-deep px-3 py-2 flex items-center justify-between gap-4"
                    data-testid={`ops-resolved-${req.id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-inter text-xs text-cream truncate">
                        {customer ? customerName(customer) : 'Unknown customer'}
                        <span className="font-serif text-cream-soft"> - &ldquo;{req.note}&rdquo;</span>
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-cream-faint mt-0.5">
                        by {label(req.requestedBy)}
                        {req.resolvedBy ? ` · resolved by ${label(req.resolvedBy)}` : ''}
                      </p>
                    </div>
                    <span
                      className={clsx(
                        'shrink-0 rounded-full px-2.5 py-1 text-[10px] font-mono font-bold uppercase tracking-widest',
                        approved ? 'bg-mint/20 text-mint' : 'bg-blood/20 text-blood',
                      )}
                    >
                      {approved ? 'Approved' : 'Declined'}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
